import { useParams, Link, useLocation, useSearchParams } from 'react-router-dom';
import { useApi } from '../../lib/useApi.js';
import { humanizeEnum } from '../../lib/fieldLabels.js';
import { useUrlPagination } from '../../lib/usePagination.js';
import { usePageMetadata } from '../../app/PageMetaContext.jsx';
import { PageHeader } from '../../app/PageHeader.jsx';
import {
  appendWorkspaceReturnContext,
  buildWorkspaceReturnTarget,
  prependWorkspaceBreadcrumb,
} from '../../lib/workspaceContext.js';
import {
  buildEnhancementReportPath,
  buildExecutiveDeckPath,
} from '../briefings/reportEntryLinks.js';
import { buildStatusPackLibraryPath } from '../status/statusPackLinks.js';
import { RelatedEvidencePreview } from '../workspaces/RelatedEvidencePreview.jsx';
import {
  PageActionStrip,
  Breadcrumbs,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  HealthIndicator,
  PageViewerControls,
} from '../../components/primitives.jsx';
import {
  KpiHeroBand,
  GaugeCard,
  FinancialWaterfall,
  InlineScoreCell,
  bandTone,
  WorkflowPipeline,
  buildPipelineSteps,
  KpiBandSkeleton,
  ChartSkeleton,
  BrandedEmptyState,
  StaggerReveal,
} from '../../components/index.js';
import { format } from '@dha-nexus/shared';
import './portfolios.redesign.css';

// Canonical portfolio lifecycle ribbon. Stage keys map onto the portfolio
// `type` / lifecycle posture; we surface the current phase from real data.
const PORTFOLIO_LIFECYCLE_ORDER = ['intake', 'planning', 'execution', 'sustainment', 'closeout'];
const PORTFOLIO_LIFECYCLE_LABELS = {
  intake: 'Intake',
  planning: 'Planning',
  execution: 'Execution',
  sustainment: 'Sustainment',
  closeout: 'Closeout',
};

// Derive the active lifecycle phase from observable delivery signals so the
// pipeline reflects real state rather than a hard-coded stage.
function inferPortfolioLifecyclePhase({ deliverySummary, type } = {}) {
  const active = Number(deliverySummary?.activeProjects || 0);
  const typeKey = String(type || '').toLowerCase();
  if (typeKey.includes('sustain') || typeKey.includes('operational')) return 'sustainment';
  if (active > 0) return 'execution';
  if (typeKey.includes('plan')) return 'planning';
  return 'intake';
}

function loeTone(value) {
  return bandTone(value, { good: 70, warn: 40 });
}

// ---- Portfolio workspace tab consolidation (FS-P3M-2, AC-P3M-2) ------------
// The 11 legacy tabs collapse into 6. Thin/stub tab content merges into its
// related group as a stacked section, and legacy ?tab= values are normalized
// onto their new home tab so existing deep links keep working.
export const PORTFOLIO_WORKSPACE_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'delivery', label: 'Delivery' },
  { key: 'money', label: 'Money' },
  { key: 'risks', label: 'Risks & Actions' },
  { key: 'decisions', label: 'Decisions & Roadmap' },
  { key: 'evidence', label: 'Evidence & AI' },
];

const PORTFOLIO_WORKSPACE_TAB_KEYS = new Set(PORTFOLIO_WORKSPACE_TABS.map((tab) => tab.key));

// Legacy 11-tab keys → new 6-tab home. Exported so tests can prove the
// old→new mapping stays total.
export const PORTFOLIO_WORKSPACE_LEGACY_TAB_MAP = {
  overview: 'overview',
  loe: 'delivery',
  analytics: 'overview',
  prioritization: 'decisions',
  roadmap: 'decisions',
  capacity: 'delivery',
  financials: 'money',
  risks: 'risks',
  decision: 'decisions',
  ai: 'evidence',
  exports: 'evidence',
};

function ConfiguredFieldHighlights({ highlights = [] }) {
  if (!Array.isArray(highlights) || highlights.length === 0) return null;
  const maskedHighlights = highlights.filter((highlight) => highlight?.masked);
  return (
    <div className="muted" style={{ marginTop: 8 }}>
      {highlights.map((highlight) => (
        <div key={highlight.fieldKey}>
          {highlight.label}: {highlight.displayValue}
        </div>
      ))}
      {maskedHighlights.length > 0 ? (
        <div>
          {maskedHighlights[0].message ||
            'Sensitive configured field data is masked for your role.'}
        </div>
      ) : null}
    </div>
  );
}

function formatFinancialSummaryAmountUsdM(value) {
  return format.formatCurrencyMillions(Number(value || 0) * 1_000_000);
}

function calculateBurnRatePercent(summary = {}) {
  if (summary?.burnRatePercent != null) return Number(summary.burnRatePercent);
  const plannedBudgetUsdM = Number(summary?.plannedBudgetUsdM || 0);
  const actualSpendUsdM = Number(summary?.actualSpendUsdM || 0);
  return plannedBudgetUsdM > 0 ? Math.round((actualSpendUsdM / plannedBudgetUsdM) * 100) : 0;
}

function formatFreshnessLabel(value) {
  return String(value || 'unknown')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function getPortfolioWorkspaceTabFromSearchParams(searchParams) {
  const requestedTab = searchParams.get('tab');
  if (PORTFOLIO_WORKSPACE_TAB_KEYS.has(requestedTab)) return requestedTab;
  return PORTFOLIO_WORKSPACE_LEGACY_TAB_MAP[requestedTab] || 'overview';
}

export function buildPortfolioWorkspaceTabSearchParams(searchParams, nextTab) {
  const resolved = PORTFOLIO_WORKSPACE_TAB_KEYS.has(nextTab)
    ? nextTab
    : PORTFOLIO_WORKSPACE_LEGACY_TAB_MAP[nextTab] || 'overview';
  const next = new URLSearchParams(searchParams);
  if (resolved === 'overview') {
    next.delete('tab');
  } else {
    next.set('tab', resolved);
  }
  return next;
}

function PortfolioProgramsPanel({ programPager, currentWorkspaceTarget, currentWorkspaceLabel }) {
  return (
    <div className="span-12 card">
      <h2 className="card-title">Programs</h2>
      {Number(programPager.page?.total || 0) === 0 ? (
        <EmptyState title="No programs" />
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Program</th>
                <th>Status</th>
                <th>Manager</th>
              </tr>
            </thead>
            <tbody>
              {programPager.items.map((g) => (
                <tr key={g.id}>
                  <td>
                    <Link
                      to={appendWorkspaceReturnContext(
                        `/programs/${g.id}`,
                        currentWorkspaceTarget,
                        currentWorkspaceLabel,
                      )}
                    >
                      {g.name}
                    </Link>
                  </td>
                  <td>
                    <HealthIndicator health={g.health?.overall || g.status} />
                  </td>
                  <td>{g.programManagerUserId || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <PageViewerControls
            page={programPager.page}
            label="programs"
            pageSize={programPager.pageSize}
            onOffsetChange={programPager.setOffset}
            onPageSizeChange={programPager.setPageSize}
          />
        </>
      )}
    </div>
  );
}

function PortfolioRelatedEvidencePanel({
  relatedEvidence,
  currentWorkspaceTarget,
  currentWorkspaceLabel,
}) {
  const counts = relatedEvidence?.data?.data?.counts;
  const actions = relatedEvidence?.data?.data?.actions || [];
  const artifacts = relatedEvidence?.data?.data?.artifacts || [];
  const briefings = relatedEvidence?.data?.data?.briefings || [];
  const auditSummary = relatedEvidence?.data?.data?.auditSummary;
  const previews = relatedEvidence?.data?.data?.previews || {};

  return (
    <div className="span-12 card">
      <h2 className="card-title">Related evidence</h2>
      {relatedEvidence?.loading ? (
        <LoadingSkeleton rows={3} />
      ) : relatedEvidence?.error ? (
        <ErrorState error={relatedEvidence.error} />
      ) : !counts ? (
        <EmptyState
          title="No related evidence"
          description="Linked actions, artifacts, briefings, AI runs, and audit posture will appear here."
        />
      ) : (
        <>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <span className="chip blue">{counts.actions} actions</span>
            <span className="chip blue">{counts.risks} risks</span>
            <span className="chip blue">{counts.artifacts} artifacts</span>
            <span className="chip blue">{counts.aiRuns} AI runs</span>
            <span className="chip blue">{counts.briefings} briefings</span>
            <span className="chip blue">{counts.comments} comments</span>
            <span className="chip outline">{counts.auditEvents} audit events</span>
          </div>
          <div
            className="grid-auto"
            style={{ display: 'grid', gap: 16, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
          >
            <RelatedEvidencePreview
              family="actions"
              label="Actions"
              emptyLabel="No linked actions in scope."
              initialItems={actions}
              preview={previews.actions}
              renderItem={(action) => (
                <div key={action.id}>
                  <Link
                    to={appendWorkspaceReturnContext(
                      action.route,
                      currentWorkspaceTarget,
                      currentWorkspaceLabel,
                    )}
                  >
                    {action.title}
                  </Link>
                </div>
              )}
            />
            <RelatedEvidencePreview
              family="artifacts"
              label="Artifacts"
              emptyLabel="No linked artifacts in scope."
              initialItems={artifacts}
              preview={previews.artifacts}
              renderItem={(artifact) => (
                <div key={artifact.id}>
                  <Link
                    to={appendWorkspaceReturnContext(
                      artifact.route,
                      currentWorkspaceTarget,
                      currentWorkspaceLabel,
                    )}
                  >
                    {artifact.title}
                  </Link>
                </div>
              )}
            />
            <div>
              <div className="muted" style={{ marginBottom: 8 }}>
                {auditSummary?.totalEvents ?? 0} audit events
              </div>
              <RelatedEvidencePreview
                family="briefings"
                label="Briefings and audit"
                emptyLabel="No linked briefings in scope."
                initialItems={briefings}
                preview={previews.briefings}
                renderItem={(brief) => (
                  <div key={brief.id} style={{ marginTop: 8 }}>
                    <Link
                      to={appendWorkspaceReturnContext(
                        brief.route,
                        currentWorkspaceTarget,
                        currentWorkspaceLabel,
                      )}
                    >
                      {brief.title}
                    </Link>
                  </div>
                )}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function PortfolioProfilePage() {
  const { id } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const programPaging = useUrlPagination(searchParams, setSearchParams, {
    defaultPageSize: 10,
    pageSizeParam: 'programPageSize',
    offsetParam: 'programOffset',
  });
  const projectPaging = useUrlPagination(searchParams, setSearchParams, {
    defaultPageSize: 10,
    pageSizeParam: 'projectPageSize',
    offsetParam: 'projectOffset',
  });
  const p = useApi(`/portfolios/${id}`);
  const dashboard = useApi(`/portfolios/${id}/dashboard`);
  const relatedEvidence = useApi(`/portfolios/${id}/related`);
  const programs = useApi(`/portfolios/${id}/programs`, {
    query: { limit: programPaging.pageSize, offset: programPaging.offset },
  });
  const prioritizedProjects = useApi(`/portfolios/${id}/projects`, {
    query: { limit: projectPaging.pageSize, offset: projectPaging.offset },
  });
  const aiRuns = useApi('/ai/runs', { query: { scopeType: 'portfolio', scopeId: id, limit: 20 } });
  const briefings = useApi('/briefings', {
    query: { scopeType: 'portfolio', scopeId: id, limit: 20 },
  });
  const statusPacks = useApi('/status-packs', {
    query: { scopeType: 'portfolio', scopeId: id, limit: 20 },
  });
  const data = p.data?.data;
  const dashboardData = dashboard.data?.data;
  const tab = getPortfolioWorkspaceTabFromSearchParams(searchParams);
  const programPager = {
    items: programs.data?.data?.items || [],
    page: programs.data?.data?.page || {
      limit: programPaging.pageSize,
      offset: programPaging.offset,
      total: 0,
    },
    pageSize: programPaging.pageSize,
    setOffset: programPaging.setOffset,
    setPageSize: programPaging.setPageSize,
  };
  const projectPager = {
    items: prioritizedProjects.data?.data?.items || [],
    page: prioritizedProjects.data?.data?.page || {
      limit: projectPaging.pageSize,
      offset: projectPaging.offset,
      total: 0,
    },
    pageSize: projectPaging.pageSize,
    setOffset: projectPaging.setOffset,
    setPageSize: projectPaging.setPageSize,
  };
  const aiRunsList = aiRuns.data?.data || [];
  const briefingsList = briefings.data?.data || [];
  const statusPacksList = statusPacks.data?.data || [];
  const currentWorkspaceTarget = buildWorkspaceReturnTarget(location);
  const currentWorkspaceLabel = data?.name || 'Portfolio workspace';
  const selectTab = (nextTab) => {
    setSearchParams(buildPortfolioWorkspaceTabSearchParams(searchParams, nextTab), {
      replace: false,
    });
  };

  usePageMetadata({
    title: data?.name || 'Portfolio',
    subtitle: data?.description,
  });

  if (p.loading || dashboard.loading || programs.loading || prioritizedProjects.loading)
    return (
      <div className="pf-redesign">
        <KpiBandSkeleton count={4} />
        <div style={{ marginTop: 16 }}>
          <ChartSkeleton height={260} />
        </div>
      </div>
    );
  if (p.error || dashboard.error || programs.error || prioritizedProjects.error)
    return (
      <ErrorState
        error={p.error || dashboard.error || programs.error || prioritizedProjects.error}
      />
    );
  if (!data)
    return (
      <BrandedEmptyState
        icon="🗂️"
        eyebrow="Portfolio workspace"
        title="Portfolio not found"
        description="This portfolio may have been retired or is outside your access scope."
        tone="slate"
      />
    );

  const lifecyclePhase = inferPortfolioLifecyclePhase(data);
  const lifecycleSteps = buildPipelineSteps({
    order: PORTFOLIO_LIFECYCLE_ORDER,
    current: lifecyclePhase,
    labels: PORTFOLIO_LIFECYCLE_LABELS,
  });
  const fin = data.financialSummary || {};
  const plannedUsdM = Number(fin.plannedBudgetUsdM || 0);
  const obligatedUsdM = Number(fin.obligatedUsdM || 0);
  const actualUsdM = Number(fin.actualSpendUsdM || 0);
  const facUsdM = Number(fin.forecastAtCompletionUsdM || 0);
  const burnRatePercent = calculateBurnRatePercent(fin);
  const confidencePercent =
    dashboardData?.decisionSupport?.confidence != null
      ? Math.round(Number(dashboardData.decisionSupport.confidence) * 100)
      : null;
  const loeRows = dashboardData?.loeAlignment || [];
  const prioritizationRows = projectPager.items;

  // Financial bridge: planned baseline → obligated/actual draws → forecast.
  const waterfallItems = [];
  if (plannedUsdM > 0) {
    waterfallItems.push({ label: 'Planned', value: plannedUsdM, type: 'baseline' });
    if (obligatedUsdM > 0)
      waterfallItems.push({
        label: 'Obligated',
        value: -(plannedUsdM - obligatedUsdM),
        type: 'delta',
      });
    if (actualUsdM > 0 && obligatedUsdM > 0)
      waterfallItems.push({
        label: 'Expended',
        value: -(obligatedUsdM - actualUsdM),
        type: 'delta',
      });
    if (facUsdM > 0) waterfallItems.push({ label: 'Forecast', value: facUsdM, type: 'forecast' });
  }

  return (
    <div className="pf-redesign">
      <Breadcrumbs
        items={prependWorkspaceBreadcrumb(
          [{ to: '/portfolios', label: 'Portfolios' }, { label: data.name }],
          searchParams,
        )}
      />
      {/* PRIME-SHELL-UX-001 revise 2: single body h1 for the detail route, read
          from the record metadata registered via usePageMetadata above. */}
      <PageHeader />
      <PageActionStrip
        filters={
          <>
            <HealthIndicator health={data.health?.overall} />
            <span className="chip outline">{data.type}</span>
          </>
        }
        actions={
          <Link
            className="btn secondary sm"
            to={buildEnhancementReportPath({ scopeType: 'portfolio', scopeId: data.id })}
          >
            Draft enhancement report
          </Link>
        }
      />
      <StaggerReveal as="section" className="bento">
        <div className="span-12 card profile-tabs-card">
          <div
            className="row"
            role="tablist"
            aria-label="Portfolio workspace tabs"
            style={{ gap: 8, flexWrap: 'wrap' }}
          >
            {PORTFOLIO_WORKSPACE_TABS.map((workspaceTab) => (
              <button
                key={workspaceTab.key}
                role="tab"
                aria-selected={tab === workspaceTab.key}
                className={`btn ${tab === workspaceTab.key ? 'primary' : 'ghost'} sm`}
                onClick={() => selectTab(workspaceTab.key)}
              >
                {workspaceTab.label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'overview' && (
          <>
            <div className="span-12">
              <KpiHeroBand
                eyebrow="Portfolio posture"
                context={
                  data.dataQualitySummary?.freshness
                    ? formatFreshnessLabel(data.dataQualitySummary.freshness)
                    : undefined
                }
                columns={4}
                items={[
                  {
                    id: 'planned',
                    label: 'Planned budget',
                    value: format.formatCurrencyMillions(plannedUsdM * 1_000_000),
                    footer:
                      obligatedUsdM > 0
                        ? `${format.formatCurrencyMillions(obligatedUsdM * 1_000_000)} obligated`
                        : undefined,
                  },
                  {
                    id: 'burn',
                    label: 'Burn rate',
                    value: burnRatePercent,
                    unit: '%',
                    status:
                      burnRatePercent > 100 ? 'brick' : burnRatePercent > 90 ? 'amber' : 'sage',
                    statusLabel:
                      burnRatePercent > 100
                        ? 'Overrun'
                        : burnRatePercent > 90
                          ? 'Watch'
                          : 'On plan',
                  },
                  {
                    id: 'active',
                    label: 'Active projects',
                    value: data.deliverySummary?.activeProjects ?? '—',
                    status: 'blue',
                  },
                  {
                    id: 'at-risk',
                    label: 'At-risk projects',
                    value: data.deliverySummary?.atRiskProjects ?? '—',
                    status:
                      Number(data.deliverySummary?.atRiskProjects || 0) > 0 ? 'brick' : 'sage',
                    statusLabel:
                      Number(data.deliverySummary?.atRiskProjects || 0) > 0 ? 'Attention' : 'Clear',
                  },
                ]}
              />
            </div>

            <div className="span-12 card">
              <h2 className="card-title">Portfolio lifecycle</h2>
              <div className="pf-pipeline-wrap">
                <WorkflowPipeline steps={lifecycleSteps} ariaLabel="Portfolio lifecycle" />
              </div>
              <p className="pf-pipeline-caption">
                Current phase: {PORTFOLIO_LIFECYCLE_LABELS[lifecyclePhase] || lifecyclePhase} ·{' '}
                {data.deliverySummary?.activeProjects ?? 0} active projects
              </p>
            </div>

            <div className="span-7 card">
              <h2 className="card-title">Mission &amp; delivery health</h2>
              <div className="pf-gauge-grid">
                {confidencePercent != null ? (
                  <GaugeCard
                    value={confidencePercent}
                    min={0}
                    max={100}
                    unit="%"
                    label="Data confidence"
                    sublabel="Decision support"
                    tone={bandTone(confidencePercent, { good: 80, warn: 60 })}
                  />
                ) : null}
                {burnRatePercent != null ? (
                  <GaugeCard
                    value={burnRatePercent}
                    min={0}
                    max={120}
                    unit="%"
                    label="Budget burn"
                    sublabel="Actual vs planned"
                    tone={burnRatePercent > 100 ? 'brick' : burnRatePercent > 90 ? 'amber' : 'sage'}
                  />
                ) : null}
              </div>
            </div>

            <div className="span-5 card">
              <h2 className="card-title">Strategic themes</h2>
              <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                {(data.strategicThemes || []).map((theme) => (
                  <span key={theme} className="chip blue">
                    {theme}
                  </span>
                ))}
              </div>
              <h2 className="card-title" style={{ marginTop: 16 }}>
                Data quality
              </h2>
              <div className="muted">
                <div>Freshness: {data.dataQualitySummary?.freshness || '—'}</div>
                <div>Confidence: {data.dataQualitySummary?.confidence ?? '—'}</div>
                <div>Open lineage issues: {data.dataQualitySummary?.openLineageIssues ?? 0}</div>
              </div>
            </div>

            <PortfolioProgramsPanel
              programPager={programPager}
              currentWorkspaceTarget={currentWorkspaceTarget}
              currentWorkspaceLabel={currentWorkspaceLabel}
            />

            <div className="span-6 card">
              <h2 className="card-title">LOE alignment</h2>
              {loeRows.length === 0 ? (
                <div className="muted">No LOE alignment data.</div>
              ) : (
                <ul className="pf-rank-list">
                  {loeRows.map((row) => (
                    <li key={row.loeId} className="pf-rank-row">
                      <div className="pf-rank-row__main">
                        <span className="pf-rank-row__name">{row.loeId}</span>
                        <span className="pf-rank-row__meta">
                          {row.projectCount} projects / {row.programCount} programs
                        </span>
                      </div>
                      <div className="pf-rank-row__score">
                        <span className="pf-rank-row__score-cap">Avg mission value</span>
                        <InlineScoreCell
                          value={row.averageMissionValueScore ?? 0}
                          tone={loeTone(row.averageMissionValueScore ?? 0)}
                          ariaLabel={`${row.loeId} average mission value ${row.averageMissionValueScore ?? 0}`}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <h2 className="card-title" style={{ marginTop: 16 }}>
                Prioritized projects
              </h2>
              {prioritizationRows.length === 0 ? (
                <div className="muted">No prioritized projects.</div>
              ) : (
                <ul className="pf-rank-list">
                  {prioritizationRows.slice(0, 4).map((project) => (
                    <li key={project.id} className="pf-rank-row">
                      <div className="pf-rank-row__main">
                        <Link
                          className="pf-rank-row__name"
                          to={appendWorkspaceReturnContext(
                            project.route,
                            currentWorkspaceTarget,
                            currentWorkspaceLabel,
                          )}
                        >
                          {project.name}
                        </Link>
                        <span className="pf-rank-row__meta">
                          Mission value {project.missionValueScore ?? 0}
                        </span>
                        <ConfiguredFieldHighlights highlights={project.configuredFieldHighlights} />
                      </div>
                      <div className="pf-rank-row__score">
                        <span className="pf-rank-row__score-cap">Alignment</span>
                        <InlineScoreCell
                          value={project.alignmentScore}
                          ariaLabel={`${project.name} alignment ${project.alignmentScore}`}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <PageViewerControls
                page={projectPager.page}
                label="projects"
                pageSize={projectPager.pageSize}
                onOffsetChange={projectPager.setOffset}
                onPageSizeChange={projectPager.setPageSize}
              />
            </div>

            <div className="span-6 card">
              <h2 className="card-title">Financial bridge</h2>
              {waterfallItems.length >= 2 ? (
                <FinancialWaterfall
                  items={waterfallItems}
                  height={240}
                  ariaLabel="Portfolio financial bridge"
                />
              ) : (
                <div className="muted">Financial bridge unavailable for this portfolio.</div>
              )}
              <p className="pf-waterfall-note">
                Planned {format.formatCurrencyMillions(plannedUsdM * 1_000_000)} · forecast at
                completion {format.formatCurrencyMillions(facUsdM * 1_000_000)} · burn{' '}
                {burnRatePercent}%
              </p>
            </div>

            <div className="span-6 card">
              <h2 className="card-title">Capacity posture</h2>
              <div className="pf-stat-strip">
                <div className="pf-stat">
                  <span className="pf-stat__value">
                    {dashboardData?.capacity?.activeAllocationCount ?? 0}
                  </span>
                  <span className="pf-stat__label">Active allocations</span>
                </div>
                <div className="pf-stat">
                  <span className="pf-stat__value">
                    {dashboardData?.capacity?.totalHoursPerWeek ?? 0}h
                  </span>
                  <span className="pf-stat__label">Hours / week</span>
                </div>
                <div className="pf-stat">
                  <span
                    className={`pf-stat__value ${
                      Number(dashboardData?.capacity?.constrainedProjectCount || 0) > 0
                        ? 'is-amber'
                        : ''
                    }`}
                  >
                    {dashboardData?.capacity?.constrainedProjectCount ?? 0}
                  </span>
                  <span className="pf-stat__label">Constrained projects</span>
                </div>
              </div>
              {(dashboardData?.capacity?.teamRows || []).length ? (
                <ul className="pf-capacity-teams">
                  {(dashboardData?.capacity?.teamRows || []).map((row) => (
                    <li key={row.teamId}>
                      <span>{row.teamName}</span>
                      <span className="muted">{row.allocationHours}h / week</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div className="span-6 card">
              <h2 className="card-title">Decision support</h2>
              <div className="pf-stat-strip">
                <div className="pf-stat">
                  <span className="pf-stat__value">
                    {dashboardData?.decisionSupport?.aiPendingReviewCount ?? 0}
                  </span>
                  <span className="pf-stat__label">AI reviews pending</span>
                </div>
                <div className="pf-stat">
                  <span
                    className={`pf-stat__value ${
                      Number(dashboardData?.decisionSupport?.openRiskCount || 0) > 0
                        ? 'is-brick'
                        : 'is-sage'
                    }`}
                  >
                    {dashboardData?.decisionSupport?.openRiskCount ?? 0}
                  </span>
                  <span className="pf-stat__label">Open risks</span>
                </div>
              </div>
              <div className="muted" style={{ marginTop: 12 }}>
                <div>Confidence: {dashboardData?.decisionSupport?.confidence ?? '—'}</div>
                <div>Freshness: {dashboardData?.decisionSupport?.freshness || '—'}</div>
              </div>
            </div>

            <PortfolioRelatedEvidencePanel
              relatedEvidence={relatedEvidence}
              currentWorkspaceTarget={currentWorkspaceTarget}
              currentWorkspaceLabel={currentWorkspaceLabel}
            />
          </>
        )}

        {tab === 'delivery' && (
          <>
            <PortfolioProgramsPanel
              programPager={programPager}
              currentWorkspaceTarget={currentWorkspaceTarget}
              currentWorkspaceLabel={currentWorkspaceLabel}
            />
            <div className="span-12 card">
              <h2 className="card-title">LOE alignment</h2>
              {loeRows.length === 0 ? (
                <BrandedEmptyState
                  icon="🎯"
                  eyebrow="Lines of effort"
                  title="No LOE alignment data"
                  description="LOE alignment and mission-value rollups will appear once projects are mapped to lines of effort."
                  tone="slate"
                />
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Line of effort</th>
                      <th>Projects</th>
                      <th>Programs</th>
                      <th>Avg mission value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loeRows.map((row) => (
                      <tr key={row.loeId}>
                        <td>{row.loeId}</td>
                        <td>{row.projectCount}</td>
                        <td>{row.programCount}</td>
                        <td>
                          <InlineScoreCell
                            value={row.averageMissionValueScore ?? 0}
                            tone={loeTone(row.averageMissionValueScore ?? 0)}
                            ariaLabel={`${row.loeId} average mission value ${row.averageMissionValueScore ?? 0}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="span-12 card">
              <h2 className="card-title">Capacity posture</h2>
              <div className="pf-stat-strip">
                <div className="pf-stat">
                  <span className="pf-stat__value">
                    {dashboardData?.capacity?.activeAllocationCount ?? 0}
                  </span>
                  <span className="pf-stat__label">Active allocations</span>
                </div>
                <div className="pf-stat">
                  <span className="pf-stat__value">
                    {dashboardData?.capacity?.totalHoursPerWeek ?? 0}h
                  </span>
                  <span className="pf-stat__label">Hours / week</span>
                </div>
                <div className="pf-stat">
                  <span
                    className={`pf-stat__value ${
                      Number(dashboardData?.capacity?.constrainedProjectCount || 0) > 0
                        ? 'is-amber'
                        : ''
                    }`}
                  >
                    {dashboardData?.capacity?.constrainedProjectCount ?? 0}
                  </span>
                  <span className="pf-stat__label">Constrained projects</span>
                </div>
              </div>
              {(dashboardData?.capacity?.teamRows || []).length ? (
                <ul className="pf-capacity-teams">
                  {(dashboardData?.capacity?.teamRows || []).map((row) => (
                    <li key={row.teamId}>
                      <span>{row.teamName}</span>
                      <span className="muted">{row.allocationHours}h / week</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </>
        )}

        {tab === 'money' && (
          <div className="span-12 card">
            <h2 className="card-title">Financial posture</h2>
            {waterfallItems.length >= 2 ? (
              <div style={{ marginBottom: 16 }}>
                <FinancialWaterfall
                  items={waterfallItems}
                  height={260}
                  ariaLabel="Portfolio financial bridge"
                />
              </div>
            ) : null}
            <div className="muted">
              <div>
                Planned:{' '}
                {formatFinancialSummaryAmountUsdM(data.financialSummary?.plannedBudgetUsdM)}
              </div>
              <div>
                Obligated: {formatFinancialSummaryAmountUsdM(data.financialSummary?.obligatedUsdM)}
              </div>
              <div>
                Expended: {formatFinancialSummaryAmountUsdM(data.financialSummary?.actualSpendUsdM)}
              </div>
              <div>
                Forecast at completion:{' '}
                {formatFinancialSummaryAmountUsdM(data.financialSummary?.forecastAtCompletionUsdM)}
              </div>
              <div>
                Variance:{' '}
                {formatFinancialSummaryAmountUsdM(data.financialSummary?.forecastVarianceUsdM)}
              </div>
              <div>Burn rate: {calculateBurnRatePercent(data.financialSummary)}%</div>
              <div>
                Source freshness:{' '}
                {formatFreshnessLabel(
                  data.dataQualitySummary?.freshness || dashboardData?.decisionSupport?.freshness,
                )}
              </div>
              <div>Active projects: {data.deliverySummary?.activeProjects ?? 0}</div>
              <div>At-risk projects: {data.deliverySummary?.atRiskProjects ?? 0}</div>
            </div>
            <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <Link
                className="btn ghost sm"
                to={appendWorkspaceReturnContext('/financials', currentWorkspaceTarget, data.name)}
              >
                Open financial workspace
              </Link>
            </div>
          </div>
        )}

        {tab === 'risks' && (
          <>
            <div className="span-12 card">
              <h2 className="card-title">Risk posture</h2>
              <div className="muted">
                <div>{dashboardData?.decisionSupport?.openRiskCount ?? 0} open risks</div>
                <div>{data.deliverySummary?.atRiskProjects ?? 0} at-risk projects</div>
                <div>
                  {dashboardData?.decisionSupport?.aiPendingReviewCount ?? 0} decisions pending
                  review
                </div>
              </div>
              <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                <Link
                  className="btn ghost sm"
                  to={appendWorkspaceReturnContext(
                    '/governance/risks',
                    currentWorkspaceTarget,
                    currentWorkspaceLabel,
                  )}
                >
                  Open risk register
                </Link>
                <Link
                  className="btn ghost sm"
                  to={appendWorkspaceReturnContext(
                    '/actions',
                    currentWorkspaceTarget,
                    currentWorkspaceLabel,
                  )}
                >
                  Open action center
                </Link>
              </div>
            </div>
            <div className="span-12 card">
              <h2 className="card-title">Linked actions</h2>
              {(relatedEvidence?.data?.data?.actions || []).length === 0 ? (
                <EmptyState
                  title="No linked actions"
                  description="Actions tied to this portfolio's programs and projects will appear here."
                />
              ) : (
                <div className="muted">
                  {(relatedEvidence?.data?.data?.actions || []).map((action) => (
                    <div key={action.id}>
                      <Link
                        to={appendWorkspaceReturnContext(
                          action.route,
                          currentWorkspaceTarget,
                          currentWorkspaceLabel,
                        )}
                      >
                        {action.title}
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {tab === 'decisions' && (
          <>
            <div className="span-12 card">
              <h2 className="card-title">Decision support</h2>
              <div className="muted">
                <div>
                  {dashboardData?.decisionSupport?.aiPendingReviewCount ?? 0} AI reviews pending
                </div>
                <div>{dashboardData?.decisionSupport?.openRiskCount ?? 0} open risks</div>
                <div>Confidence: {dashboardData?.decisionSupport?.confidence ?? '—'}</div>
                <div>Freshness: {dashboardData?.decisionSupport?.freshness || '—'}</div>
              </div>
            </div>
            <div className="span-12 card">
              <h2 className="card-title">Prioritized projects</h2>
              {Number(projectPager.page?.total || 0) === 0 ? (
                <EmptyState
                  title="No prioritized projects"
                  description="Prioritized portfolio work will appear here once portfolio scoring is available."
                />
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Mission value</th>
                      <th>Alignment score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prioritizationRows.map((project) => (
                      <tr key={project.id}>
                        <td>
                          <Link
                            to={appendWorkspaceReturnContext(
                              project.route,
                              currentWorkspaceTarget,
                              currentWorkspaceLabel,
                            )}
                          >
                            {project.name}
                          </Link>
                          <ConfiguredFieldHighlights
                            highlights={project.configuredFieldHighlights}
                          />
                        </td>
                        <td>
                          <InlineScoreCell
                            value={project.missionValueScore ?? 0}
                            ariaLabel={`${project.name} mission value ${project.missionValueScore ?? 0}`}
                          />
                        </td>
                        <td>
                          <InlineScoreCell
                            value={project.alignmentScore}
                            ariaLabel={`${project.name} alignment ${project.alignmentScore}`}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <PageViewerControls
                page={projectPager.page}
                label="projects"
                pageSize={projectPager.pageSize}
                onOffsetChange={projectPager.setOffset}
                onPageSizeChange={projectPager.setPageSize}
              />
            </div>
            <div className="span-12 card">
              <h2 className="card-title">Roadmap posture</h2>
              {Number(programPager.page?.total || 0) === 0 ? (
                <EmptyState
                  title="No roadmap posture"
                  description="Program sequencing will appear once programs are registered in this portfolio."
                />
              ) : (
                <div className="muted">
                  {programPager.items.map((program) => (
                    <div key={program.id}>
                      <Link
                        to={appendWorkspaceReturnContext(
                          `/programs/${program.id}`,
                          currentWorkspaceTarget,
                          currentWorkspaceLabel,
                        )}
                      >
                        {program.name}
                      </Link>
                      : {program.status || 'Status pending'}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {tab === 'evidence' && (
          <div className="span-12 card">
            <h2 className="card-title">AI posture</h2>
            {aiRuns.loading ? (
              <LoadingSkeleton rows={3} />
            ) : aiRuns.error ? (
              <ErrorState error={aiRuns.error} />
            ) : aiRunsList.length === 0 ? (
              <EmptyState
                title="No portfolio AI runs"
                description="Portfolio-scoped AI reviews will appear here when governed runs are created."
              />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Status</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {aiRunsList.map((run) => (
                    <tr key={run.id}>
                      <td>
                        <Link to={`/ai/runs/${run.id}`}>{run.name || run.id}</Link>
                      </td>
                      <td title={run.reviewState || run.status || undefined}>
                        {humanizeEnum(run.reviewState || run.status, 'Pending review')}
                      </td>
                      <td>{run.confidence ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'evidence' && (
          <div className="span-12 card">
            <div
              className="row"
              style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
            >
              <h2 className="card-title" style={{ margin: 0 }}>
                Export products
              </h2>
              <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <Link
                  className="btn ghost sm"
                  to={buildExecutiveDeckPath({ scopeType: 'portfolio', scopeId: id })}
                >
                  Draft executive deck
                </Link>
                <Link
                  className="btn secondary sm"
                  to={buildEnhancementReportPath({ scopeType: 'portfolio', scopeId: id })}
                >
                  Draft enhancement report
                </Link>
                <Link
                  className="btn outline sm"
                  to={buildStatusPackLibraryPath({
                    scopeType: 'portfolio',
                    scopeId: id,
                    scopeLabel: data?.name || 'Portfolio workspace',
                    title: `${data?.name || 'Portfolio'} stakeholder status`,
                  })}
                >
                  Draft status pack
                </Link>
              </div>
            </div>
            {briefings.loading ? (
              <LoadingSkeleton rows={3} />
            ) : briefings.error ? (
              <ErrorState error={briefings.error} />
            ) : briefingsList.length === 0 ? (
              <EmptyState
                title="No exported products"
                description="Generated portfolio products will appear here."
              />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {briefingsList.map((brief) => (
                    <tr key={brief.id}>
                      <td>
                        <Link to={`/briefings/${brief.id}`}>
                          {brief.title || brief.name || brief.id}
                        </Link>
                      </td>
                      <td>{brief.status || 'Draft'}</td>
                      <td>{brief.updatedAt || brief.generatedAt || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <h3 className="card-title" style={{ marginTop: 16 }}>
              Stakeholder status packs
            </h3>
            {statusPacks.loading ? (
              <LoadingSkeleton rows={2} />
            ) : statusPacks.error ? (
              <ErrorState error={statusPacks.error} />
            ) : statusPacksList.length === 0 ? (
              <EmptyState
                title="No status packs"
                description="Portfolio-scoped stakeholder packs will appear here after draft creation and publishing."
              />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Status pack</th>
                    <th>Status</th>
                    <th>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {statusPacksList.map((pack) => (
                    <tr key={pack.id}>
                      <td>
                        <Link to={`/status-packs/${pack.id}`}>{pack.title || pack.id}</Link>
                      </td>
                      <td>{pack.status || 'Draft'}</td>
                      <td>{pack.shareId || 'Not published'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </StaggerReveal>
    </div>
  );
}
